import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import {
    Receipt, Loader2, ChevronLeft, ChevronRight, Calendar, CalendarClock, Search, Wallet,
    CheckCircle2, XCircle, ExternalLink, Eye, Users, BookOpen, Download, Banknote, RefreshCw,
    AlertCircle, TriangleAlert, GraduationCap, RotateCcw, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
    useAdminOutputBillings,
    useAdminOutputBilling,
    useExtendOutputBilling,
    useBulkExtendOutputBillings,
    exportOutputBillingsExcel,
    useSyncOutputBillings,
    type OutputSyncCheckedDetail,
} from "@/services/adminConferenceOutputService";
import type { ConferenceOutputBilling } from "@/services/conferenceOutputPublicService";
import { StatCard } from "@/pages/private/admin/conference-output/OutputDecisionsPage";

const formatDateTime = (val?: string | null) => (val ? new Date(val).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

const correspondingAuthorName = (b: ConferenceOutputBilling): string => {
    const authors = b.registration?.authors;
    if (!authors || authors.length === 0) return "-";
    const main = authors.find((a) => a.is_corresponding) || authors[0];
    return `${main.given_name} ${main.family_name || ""}`.trim();
};

export default function OutputBillingsPage() {
    const [status, setStatus] = useState<string>("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);
    const [conferenceId, setConferenceId] = useState<string>("all");
    const confParam = conferenceId === "all" ? undefined : conferenceId;

    const { data: conferences } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        },
    });

    const { data, isLoading } = useAdminOutputBillings({ status: status === "all" ? undefined : status, conference_id: confParam, search: debouncedSearch || undefined, page, per_page: perPage });
    const extendBilling = useExtendOutputBilling();

    // Kartu total per status - reuse endpoint index dengan per_page:1
    const { data: totalAll } = useAdminOutputBillings({ conference_id: confParam, per_page: 1, page: 1 });
    const { data: totalUnpaid } = useAdminOutputBillings({ status: "unpaid", conference_id: confParam, per_page: 1, page: 1 });
    const { data: totalPaid } = useAdminOutputBillings({ status: "paid", conference_id: confParam, per_page: 1, page: 1 });
    const { data: totalExpired } = useAdminOutputBillings({ status: "expired", conference_id: confParam, per_page: 1, page: 1 });

    const [extendTarget, setExtendTarget] = useState<ConferenceOutputBilling | null>(null);
    const [extendDate, setExtendDate] = useState("");
    const [extendReason, setExtendReason] = useState("");
    const [detailId, setDetailId] = useState<number | null>(null);
    const { data: detailData, isLoading: isLoadingDetail } = useAdminOutputBilling(detailId ?? undefined);

    // --- Export Dialog ---
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [exportConferenceId, setExportConferenceId] = useState("all");
    const [exportStatus, setExportStatus] = useState("all");
    const [exportDateFrom, setExportDateFrom] = useState("");
    const [exportDateTo, setExportDateTo] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    // --- Bulk Extend Dialog ---
    const bulkExtendMutation = useBulkExtendOutputBillings();
    const [isBulkExtendOpen, setIsBulkExtendOpen] = useState(false);
    const [bulkExtendConferenceId, setBulkExtendConferenceId] = useState("all");
    const [bulkExtendStatus, setBulkExtendStatus] = useState<"all" | "unpaid" | "expired">("all");
    const [bulkExtendDate, setBulkExtendDate] = useState("");
    const [bulkExtendReason, setBulkExtendReason] = useState("Perpanjangan massal batas pembayaran");
    const [bulkExtendSendEmail, setBulkExtendSendEmail] = useState(true);

    const handleExecuteBulkExtend = async () => {
        if (!bulkExtendDate) {
            toast.error("Silakan tentukan batas waktu kedaluwarsa baru.");
            return;
        }

        try {
            const res = await bulkExtendMutation.mutateAsync({
                conference_id: bulkExtendConferenceId === "all" ? undefined : bulkExtendConferenceId,
                status: bulkExtendStatus,
                new_expired_at: bulkExtendDate,
                reason: bulkExtendReason || undefined,
                send_email: bulkExtendSendEmail,
            });

            toast.success(res.message || "Perpanjangan massal tagihan berhasil diproses.");
            setIsBulkExtendOpen(false);
            setBulkExtendDate("");
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal memproses perpanjangan massal tagihan.");
        }
    };

    const handleOpenExport = () => {
        setExportConferenceId(conferenceId || "all");
        setExportStatus(status || "all");
        setIsExportOpen(true);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportOutputBillingsExcel({
                status: exportStatus,
                date_from: exportDateFrom,
                date_to: exportDateTo,
                conference_id: exportConferenceId === "all" ? undefined : exportConferenceId,
            });
            toast.success("File Excel berhasil diunduh");
            setIsExportOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mengunduh file export");
        } finally {
            setIsExporting(false);
        }
    };

    // --- Sync Payment Gateway ---
    const syncMutation = useSyncOutputBillings();
    const syncResult = syncMutation.data ?? null;

    const handleSync = () => {
        syncMutation.mutate(undefined, {
            onSuccess: (res) => {
                const failedCount = res.failed_count ?? 0;
                if (failedCount > 0) {
                    toast(`${res.message || "Sinkronisasi selesai"} - buka detail untuk lihat VA yang gagal.`, { icon: <TriangleAlert className="w-5 h-5 text-amber-500" />, duration: 6000 });
                } else {
                    toast.success(res.message || "Sinkronisasi Selesai");
                }
            },
            onError: (error: any) => {
                toast.error(error?.response?.data?.message || "Gagal melakukan sinkronisasi pembayaran dari Payment Gateway.");
            },
        });
    };

    const billings: ConferenceOutputBilling[] = data?.data ?? [];
    const filtered: ConferenceOutputBilling[] = billings;

    const handleExtend = async () => {
        if (!extendTarget || !extendDate) return;
        try {
            await extendBilling.mutateAsync({ id: extendTarget.id, new_expired_at: extendDate, reason: extendReason });
            toast.success("Tagihan berhasil diperpanjang");
            setExtendTarget(null);
            setExtendDate("");
            setExtendReason("");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal memperpanjang tagihan");
        }
    };

    const statusBadge = (s: string) => {
        const map: Record<string, string> = {
            unpaid: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
            paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400",
            expired: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
            cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
        };
        return <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md ${map[s] || ""}`}>{s}</span>;
    };

    const formatCurrency = (amount: string | number, currency: string) =>
        new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", { style: "currency", currency, minimumFractionDigits: 0 }).format(Number(amount));

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col gap-3 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <Receipt className="w-64 h-64 text-blue-600" />
                </div>
                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm">
                        <Receipt className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Tagihan Publikasi Proceeding</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-none">
                            Monitoring status pembayaran biaya publikasi prosiding penulis.
                        </p>
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 relative z-10 pt-5 border-t border-slate-100 dark:border-slate-800">
                    {/* Grup Kiri: Alat Data & Laporan */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            onClick={handleOpenExport}
                            variant="outline"
                            className="h-10 px-3.5 sm:px-4 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300 shadow-sm text-sm"
                        >
                            <Download className="w-4 h-4 mr-2 text-slate-500" /> Export Excel
                        </Button>
                    </div>

                    {/* Grup Kanan: Aksi Operasional Utama */}
                    <div className="flex items-center gap-2.5 flex-wrap justify-start lg:justify-end">
                        <Button
                            onClick={handleSync}
                            disabled={syncMutation.isPending}
                            variant="outline"
                            className="h-10 px-3.5 sm:px-4 rounded-xl border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 shadow-sm text-sm"
                        >
                            {syncMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-600" /> : <RefreshCw className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />}
                            <span>{syncMutation.isPending ? "Menyinkronkan..." : "Sync Payment Gateway"}</span>
                        </Button>

                        <Button
                            onClick={() => {
                                setBulkExtendConferenceId(conferenceId || "all");
                                setBulkExtendStatus(status === "unpaid" || status === "expired" ? status : "all");
                                setIsBulkExtendOpen(true);
                            }}
                            className="h-10 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-sm text-sm"
                        >
                            <CalendarClock className="w-4 h-4 mr-2" />
                            <span>Perpanjang Tagihan</span>
                            <span className="hidden xl:inline">&nbsp;Massal</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Rincian Nominal - total, sudah dibayar, belum dibayar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-5 shadow-lg flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-white/15 text-white shrink-0"><Banknote className="w-6 h-6" /></div>
                    <div className="min-w-0">
                        <p className="text-2xl font-black text-white leading-tight truncate">
                            {totalAll ? formatCurrency(totalAll.sum_amount, "IDR") : <Loader2 className="w-5 h-5 animate-spin text-white/60" />}
                        </p>
                        <p className="text-xs font-semibold text-blue-100 mt-0.5">Total Nilai Seluruh Tagihan</p>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-5 shadow-lg flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-white/15 text-white shrink-0"><CheckCircle2 className="w-6 h-6" /></div>
                    <div className="min-w-0">
                        <p className="text-2xl font-black text-white leading-tight truncate">
                            {totalPaid ? formatCurrency(totalPaid.sum_amount, "IDR") : <Loader2 className="w-5 h-5 animate-spin text-white/60" />}
                        </p>
                        <p className="text-xs font-semibold text-emerald-100 mt-0.5">Sudah Dibayar ({totalPaid?.total ?? 0} tagihan)</p>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl p-5 shadow-lg flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-white/15 text-white shrink-0"><Wallet className="w-6 h-6" /></div>
                    <div className="min-w-0">
                        <p className="text-2xl font-black text-white leading-tight truncate">
                            {totalUnpaid ? formatCurrency(totalUnpaid.sum_amount, "IDR") : <Loader2 className="w-5 h-5 animate-spin text-white/60" />}
                        </p>
                        <p className="text-xs font-semibold text-amber-100 mt-0.5">Belum Dibayar ({totalUnpaid?.total ?? 0} tagihan)</p>
                    </div>
                </div>
            </div>

            {/* Kartu Total */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Receipt} label="Total Tagihan" value={totalAll?.total} color="blue" />
                <StatCard icon={Wallet} label="Belum Dibayar" value={totalUnpaid?.total} color="amber" />
                <StatCard icon={CheckCircle2} label="Sudah Dibayar" value={totalPaid?.total} color="emerald" />
                <StatCard icon={XCircle} label="Kedaluwarsa" value={totalExpired?.total} color="slate" />
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-4 sm:p-5 md:p-6 bg-slate-50/60 dark:bg-slate-900/40 border-b border-slate-200/70 dark:border-slate-800 space-y-4">
                    {/* Baris 1: Pencarian, Total Info, & Tombol Reset */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-slate-400" />
                            </div>
                            <Input
                                type="text"
                                placeholder="Cari no. invoice, kode VA, atau nama/email penulis..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 pr-9 h-10 w-full text-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl shadow-2xs focus-visible:ring-1 focus-visible:ring-blue-500"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch("")}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                            {(conferenceId !== "all" || status !== "all" || !!search) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setSearch("");
                                        setConferenceId("all");
                                        setStatus("all");
                                        setPage(1);
                                    }}
                                    className="h-10 px-3.5 rounded-xl border-rose-200 dark:border-rose-900/50 bg-rose-50/70 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-950/40 text-xs font-semibold transition-colors shadow-2xs"
                                >
                                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                    Reset Filter ({[conferenceId !== "all", status !== "all", !!search].filter(Boolean).length})
                                </Button>
                            )}
                            <div className="h-10 px-3.5 flex items-center gap-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300 shadow-2xs">
                                <span className="text-slate-400">Total:</span>
                                <span className="font-bold text-slate-900 dark:text-white">{data?.total ?? 0}</span>
                                <span className="text-slate-400">Tagihan</span>
                            </div>
                        </div>
                    </div>

                    {/* Baris 2: Grid Filter Sejajar Proporsional dengan Label & Ikon */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                        {/* 1. Track */}
                        <div className="w-full space-y-1">
                            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                <GraduationCap className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                <span>Track Conference</span>
                            </label>
                            <Select value={conferenceId} onValueChange={(v) => { setConferenceId(v); setPage(1); }}>
                                <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                    <SelectValue placeholder="Semua Track" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl max-h-[300px]">
                                    <SelectItem value="all">Semua Track</SelectItem>
                                    {conferences?.map((conf: any) => (
                                        <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>{conf.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* 2. Status Tagihan */}
                        <div className="w-full space-y-1">
                            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                                <Wallet className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <span>Status Tagihan</span>
                            </label>
                            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                                <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                                    <SelectValue placeholder="Semua Status" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    <SelectItem value="all">Semua Status</SelectItem>
                                    <SelectItem value="unpaid">Unpaid (Belum Dibayar)</SelectItem>
                                    <SelectItem value="paid">Paid (Sudah Dibayar)</SelectItem>
                                    <SelectItem value="expired">Expired (Kadaluarsa)</SelectItem>
                                    <SelectItem value="cancelled">Cancelled (Dibatalkan)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Baris 3: Filter Chips / Tag Aktif */}
                    {(conferenceId !== "all" || status !== "all" || !!search) && (
                        <div className="flex items-center gap-2 flex-wrap pt-2.5 border-t border-slate-200/60 dark:border-slate-800 text-xs">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Filter Aktif:</span>
                            {conferenceId !== "all" && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800 font-medium text-xs shadow-2xs">
                                    <GraduationCap className="w-3 h-3 text-blue-500" />
                                    Track: {conferences?.find((c: any) => String(c.conference_id) === String(conferenceId))?.name || conferenceId}
                                    <button onClick={() => { setConferenceId("all"); setPage(1); }} className="hover:text-blue-900 ml-0.5 p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {status !== "all" && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-medium text-xs shadow-2xs">
                                    <Wallet className="w-3 h-3 text-indigo-500" />
                                    Status: {status.toUpperCase()}
                                    <button onClick={() => { setStatus("all"); setPage(1); }} className="hover:text-indigo-900 ml-0.5 p-0.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-800 transition-colors">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {search && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700 font-medium text-xs shadow-2xs">
                                    <Search className="w-3 h-3 text-slate-400" />
                                    Pencarian: "{search}"
                                    <button onClick={() => setSearch("")} className="hover:text-slate-900 ml-0.5 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1100px]">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold">No. Invoice / VA</th>
                                <th className="px-6 py-4 font-semibold">Penulis & Paper</th>
                                <th className="px-6 py-4 font-semibold text-center">Jumlah</th>
                                <th className="px-6 py-4 font-semibold text-center">Status</th>
                                <th className="px-6 py-4 font-semibold">Dibuat</th>
                                <th className="px-6 py-4 font-semibold">Kedaluwarsa</th>
                                <th className="px-6 py-4 font-semibold text-right w-32 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading ? (
                                <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />Memuat data...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500">Data tidak ditemukan.</td></tr>
                            ) : (
                                filtered.map((b) => {
                                    const token = b.registration?.public_token;
                                    return (
                                    <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-mono text-sm font-bold text-slate-900 dark:text-white">{b.invoice_number}</div>
                                            <div className="font-mono text-xs text-slate-500 dark:text-slate-400">ID Customer: {b.customer_number || "-"}</div>
                                        </td>
                                        <td className="px-6 py-4 max-w-xs">
                                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{correspondingAuthorName(b)}</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 truncate" title={b.registration?.paper_title_final || ""}>
                                                {b.registration?.paper_title_final || "-"}
                                            </div>
                                            {b.registration?.decision?.conference?.name && (
                                                <div className="text-[11px] text-indigo-500 dark:text-indigo-400 truncate">{b.registration.decision.conference.name}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center font-mono text-sm">{formatCurrency(b.amount, b.currency)}</td>
                                        <td className="px-6 py-4 text-center">{statusBadge(b.status)}</td>
                                        <td className="px-6 py-4 text-xs text-slate-500">{formatDateTime(b.created_at)}</td>
                                        <td className="px-6 py-4 text-xs text-slate-500">{formatDateTime(b.expired_at_payment)}</td>
                                        <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">
                                            <div className="flex items-center justify-end gap-1">
                                                <TooltipProvider delayDuration={200}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => setDetailId(b.id)}
                                                                className="h-8 w-8 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent><p>Lihat detail</p></TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                                {token && (
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    asChild
                                                                    className="h-8 w-8 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400"
                                                                >
                                                                    <a href={`${window.location.origin}/conference-output/billing/${token}`} target="_blank" rel="noreferrer">
                                                                        <ExternalLink className="w-4 h-4" />
                                                                    </a>
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Buka link tagihan</p></TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {(b.status === "expired" || b.status === "unpaid") && (
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => { setExtendTarget(b); setExtendDate(""); }}
                                                                    className="h-8 w-8 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400"
                                                                >
                                                                    <Calendar className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Perpanjang tagihan</p></TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {data && data.total > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <span className="text-slate-500 font-medium">Total {data.total} data</span>
                        <div className="flex items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-8 px-3 rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium min-w-[3rem] text-center">{data.current_page} / {data.last_page}</span>
                                <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page === data.last_page} className="h-8 px-3 rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={!!extendTarget} onOpenChange={() => setExtendTarget(null)}>
                <DialogContent className="sm:max-w-[420px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-600" /> Perpanjang Tagihan</DialogTitle>
                        <DialogDescription>{extendTarget?.invoice_number}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Waktu Kedaluwarsa Baru</Label>
                            <Input type="datetime-local" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} />
                        </div>
                        <div>
                            <Label className="mb-2 block">Alasan (opsional)</Label>
                            <Input value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Contoh: kendala transfer bank" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExtendTarget(null)} className="rounded-xl">Batal</Button>
                        <Button onClick={handleExtend} disabled={extendBilling.isPending || !extendDate} className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white">
                            {extendBilling.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Extend Billings Dialog */}
            <Dialog open={isBulkExtendOpen} onOpenChange={setIsBulkExtendOpen}>
                <DialogContent className="sm:max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                            <CalendarClock className="w-5 h-5 text-amber-600" /> Perpanjang Tagihan Massal
                        </DialogTitle>
                        <DialogDescription>
                            Perbarui batas waktu pembayaran & generate ulang VA baru di Payment Gateway untuk tagihan belum lunas / kedaluwarsa.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-1">
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Target Track Konferensi</Label>
                            <Select value={bulkExtendConferenceId} onValueChange={setBulkExtendConferenceId}>
                                <SelectTrigger className="h-10 rounded-xl">
                                    <SelectValue placeholder="Semua Track Conference" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl max-h-[300px]">
                                    <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                    {conferences?.map((c: any) => (
                                        <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Filter Status Tagihan</Label>
                            <Select value={bulkExtendStatus} onValueChange={(v: any) => setBulkExtendStatus(v)}>
                                <SelectTrigger className="h-10 rounded-xl">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Belum Lunas & Kedaluwarsa (Unpaid & Expired)</SelectItem>
                                    <SelectItem value="unpaid">Hanya Unpaid (Belum Lunas)</SelectItem>
                                    <SelectItem value="expired">Hanya Expired (Sudah Kedaluwarsa)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Waktu Kedaluwarsa Baru</Label>
                            <Input
                                type="datetime-local"
                                value={bulkExtendDate}
                                onChange={(e) => setBulkExtendDate(e.target.value)}
                                className="h-10 rounded-xl font-medium"
                            />
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Alasan Perpanjangan</Label>
                            <Input
                                value={bulkExtendReason}
                                onChange={(e) => setBulkExtendReason(e.target.value)}
                                placeholder="Contoh: Perpanjangan batas akhir pembayaran naskah prosiding"
                                className="h-10 rounded-xl"
                            />
                        </div>

                        <label className="flex items-start gap-3 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={bulkExtendSendEmail}
                                onChange={(e) => setBulkExtendSendEmail(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div className="space-y-0.5">
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Kirim email tagihan baru</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Otomatis mengirimkan email berisi Nomor VA baru dan batas waktu baru kepada penulis terdampak.
                                </p>
                            </div>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsBulkExtendOpen(false)} className="rounded-xl">
                            Batal
                        </Button>
                        <Button
                            onClick={handleExecuteBulkExtend}
                            disabled={bulkExtendMutation.isPending || !bulkExtendDate}
                            className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                        >
                            {bulkExtendMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
                            Proses Perpanjangan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!detailId} onOpenChange={() => setDetailId(null)}>
                <DialogContent className="sm:max-w-[560px] bg-white dark:bg-slate-900 rounded-3xl p-0 overflow-hidden max-h-[85vh] overflow-y-auto">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="flex items-center gap-2 text-slate-800 dark:text-white"><Receipt className="w-5 h-5 text-blue-600" /> Detail Tagihan</DialogTitle>
                        <DialogDescription>{detailData?.invoice_number}</DialogDescription>
                    </DialogHeader>

                    {isLoadingDetail ? (
                        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                    ) : detailData ? (
                        <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                {statusBadge(detailData.status)}
                                <span className="font-mono text-lg font-black text-slate-900 dark:text-white">{formatCurrency(detailData.amount, detailData.currency)}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                                    <p className="text-slate-400 uppercase font-bold tracking-wide mb-0.5">No. Invoice / Nomor VA</p>
                                    <p className="font-mono font-semibold text-slate-800 dark:text-slate-200">{detailData.invoice_number}</p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                                    <p className="text-slate-400 uppercase font-bold tracking-wide mb-0.5">ID Customer</p>
                                    <p className="font-mono font-semibold text-slate-800 dark:text-slate-200">{detailData.customer_number || "-"}</p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                                    <p className="text-slate-400 uppercase font-bold tracking-wide mb-0.5">Dibuat</p>
                                    <p className="font-semibold text-slate-800 dark:text-slate-200">{formatDateTime(detailData.created_at)}</p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                                    <p className="text-slate-400 uppercase font-bold tracking-wide mb-0.5">Kedaluwarsa</p>
                                    <p className="font-semibold text-slate-800 dark:text-slate-200">{formatDateTime(detailData.expired_at_payment)}</p>
                                </div>
                                {detailData.status === "paid" && (
                                    <>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                                            <p className="text-emerald-500 uppercase font-bold tracking-wide mb-0.5">Dibayar pada</p>
                                            <p className="font-semibold text-emerald-700 dark:text-emerald-400">{formatDateTime(detailData.created_at_payment)}</p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                                            <p className="text-emerald-500 uppercase font-bold tracking-wide mb-0.5">Receipt dikirim</p>
                                            <p className="font-semibold text-emerald-700 dark:text-emerald-400">{detailData.receipt_sent_at ? formatDateTime(detailData.receipt_sent_at) : "Belum"}</p>
                                        </div>
                                    </>
                                )}
                                {detailData.fee?.publisher && (
                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 col-span-2">
                                        <p className="text-slate-400 uppercase font-bold tracking-wide mb-0.5">Publisher</p>
                                        <p className="font-semibold text-slate-800 dark:text-slate-200">{detailData.fee.publisher}</p>
                                    </div>
                                )}
                            </div>

                            {detailData.extension_count > 0 && (
                                <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-2xl p-4 text-xs">
                                    <span className="text-xs font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1.5 mb-1.5"><Calendar className="w-3.5 h-3.5" /> Riwayat Perpanjangan</span>
                                    <p className="text-slate-600 dark:text-slate-300">Diperpanjang {detailData.extension_count}x, terakhir {formatDateTime(detailData.last_extended_at)}</p>
                                    {detailData.extend_reason && <p className="text-slate-500 dark:text-slate-400 mt-1">Alasan: {detailData.extend_reason}</p>}
                                </div>
                            )}

                            {detailData.registration && (
                                <div className="bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-2xl p-4 space-y-2">
                                    <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Paper</span>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{detailData.registration.paper_title_final || "-"}</p>
                                    {detailData.registration.decision?.conference?.name && (
                                        <p className="text-xs text-indigo-500 dark:text-indigo-400">{detailData.registration.decision.conference.name}</p>
                                    )}

                                    {(detailData.registration.authors?.length ?? 0) > 0 && (
                                        <div className="pt-2 mt-2 border-t border-purple-100 dark:border-purple-900/30">
                                            <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1.5"><Users className="w-3.5 h-3.5" /> Daftar Penulis</span>
                                            <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                                                {detailData.registration.authors?.map((a) => (
                                                    <li key={a.id}>
                                                        {a.given_name} {a.family_name} {a.is_corresponding && <span className="text-purple-600 font-semibold">(Corresponding)</span>}
                                                        {a.affiliation && <span className="text-slate-400"> - {a.affiliation}</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {detailData.registration.public_token && (
                                        <div className="pt-3 mt-3 border-t border-purple-100 dark:border-purple-900/30 space-y-1.5">
                                            <span className="text-xs font-bold uppercase tracking-widest text-purple-500 flex items-center gap-1.5 mb-1"><ExternalLink className="w-3.5 h-3.5" /> Link Tagihan (Dikirim via Email)</span>
                                            <a
                                                href={`${window.location.origin}/conference-output/billing/${detailData.registration.public_token}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] text-purple-600 dark:text-purple-400 hover:underline break-all block bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-purple-100 dark:border-purple-800"
                                            >
                                                {`${window.location.origin}/conference-output/billing/${detailData.registration.public_token}`}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* Export Dialog */}
            <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
                <DialogContent className="sm:max-w-[440px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-emerald-600" /> Export Data Tagihan</DialogTitle>
                        <DialogDescription>Pilih filter track, status, dan/atau rentang tanggal dibuat sebelum mengunduh file Excel.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Track Conference</Label>
                            <Select value={exportConferenceId} onValueChange={setExportConferenceId}>
                                <SelectTrigger className="w-full h-11 rounded-xl"><SelectValue placeholder="Semua Track Conference" /></SelectTrigger>
                                <SelectContent className="rounded-xl max-h-[300px]">
                                    <SelectItem value="all">Semua Track Conference</SelectItem>
                                    {conferences?.map((conf: any) => (
                                        <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>{conf.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-slate-400 mt-1">Kosongkan / pilih &apos;Semua Track Conference&apos; agar data semua track ter-export.</p>
                        </div>
                        <div>
                            <Label className="mb-2 block">Status</Label>
                            <Select value={exportStatus} onValueChange={setExportStatus}>
                                <SelectTrigger className="w-full h-11 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Status</SelectItem>
                                    <SelectItem value="unpaid">Unpaid</SelectItem>
                                    <SelectItem value="paid">Paid</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="mb-2 block">Dibuat Dari</Label>
                                <Input type="date" value={exportDateFrom} onChange={(e) => setExportDateFrom(e.target.value)} />
                            </div>
                            <div>
                                <Label className="mb-2 block">Sampai</Label>
                                <Input type="date" value={exportDateTo} onChange={(e) => setExportDateTo(e.target.value)} />
                            </div>
                        </div>
                        <p className="text-xs text-slate-400">Kosongkan rentang tanggal untuk mengekspor semua data (tanpa batasan tanggal dibuat).</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsExportOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={handleExport} disabled={isExporting} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">
                            {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Unduh Excel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Hasil Sync Payment Gateway */}
            <Dialog open={!!syncResult} onOpenChange={(open) => !open && syncMutation.reset()}>
                <DialogContent className="!max-w-[95vw] sm:!max-w-2xl lg:!max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col rounded-[2rem] p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
                    <DialogHeader className="shrink-0 p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 text-left">
                        <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-blue-600" /> Detail Hasil Sync Payment Gateway
                        </DialogTitle>
                        <p className="text-xs text-slate-500 mt-1">
                            {syncResult?.total_va_checked ?? 0} VA dicek, {syncResult?.total_participant_updated ?? 0} tagihan diperbarui jadi lunas
                            {(syncResult?.failed_count ?? 0) > 0 ? `, ${syncResult?.failed_count} VA gagal dicek.` : "."}
                        </p>
                    </DialogHeader>

                    <div className="overflow-y-auto flex-1">
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur border-b border-slate-100 dark:border-slate-800 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    <th className="py-3 px-4 sm:px-6 w-12">No</th>
                                    <th className="py-3 px-4 sm:px-6">Nomor VA / Nama</th>
                                    <th className="py-3 px-4 sm:px-6 text-center">Status Sebelum</th>
                                    <th className="py-3 px-4 sm:px-6 text-center">Status Sesudah</th>
                                    <th className="py-3 px-4 sm:px-6">Keterangan</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-sm">
                                {(syncResult?.checked_details ?? [])
                                    .slice()
                                    .sort((a, b) => {
                                        const rank = (d: OutputSyncCheckedDetail) => (d.updated ? 0 : d.error ? 1 : 2);
                                        return rank(a) - rank(b);
                                    })
                                    .map((detail, idx) => {
                                        const syncStatusBadge = (status?: string | null) => {
                                            if (!status) return <span className="text-slate-400 text-xs">-</span>;
                                            const cls = status === "paid"
                                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                                                : status === "expired"
                                                    ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700"
                                                    : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800";
                                            const label = status === "paid" ? "Lunas" : status === "expired" ? "Kedaluwarsa" : "Belum Lunas";
                                            return (
                                                <Badge className={cn("font-bold px-2.5 py-0.5 shadow-none", cls)}>
                                                    {label}
                                                </Badge>
                                            );
                                        };

                                        return (
                                            <tr key={`${detail.customer_number}-${idx}`} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                                <td className="py-3 px-4 sm:px-6 font-bold text-slate-500 dark:text-slate-400">{idx + 1}</td>
                                                <td className="py-3 px-4 sm:px-6">
                                                    <div className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300">{detail.customer_number}</div>
                                                    <div className="text-xs text-slate-500 mt-0.5">{detail.buyer_name || "-"}</div>
                                                </td>
                                                <td className="py-3 px-4 sm:px-6 text-center">{syncStatusBadge(detail.previous_status)}</td>
                                                <td className="py-3 px-4 sm:px-6 text-center">{syncStatusBadge(detail.new_status)}</td>
                                                <td className="py-3 px-4 sm:px-6">
                                                    {detail.updated ? (
                                                        <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center gap-1">
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> Diperbarui jadi Lunas
                                                        </span>
                                                    ) : detail.error ? (
                                                        <span className="text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center gap-1">
                                                            <AlertCircle className="w-3.5 h-3.5" /> {detail.error}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400 text-xs">Tidak ada perubahan</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                {(!syncResult?.checked_details || syncResult.checked_details.length === 0) && (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center max-w-sm mx-auto text-slate-400">
                                                <Wallet className="w-10 h-10 mb-3 opacity-20" />
                                                <p className="text-sm font-medium">Tidak ada detail VA untuk ditampilkan.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <DialogFooter className="shrink-0 p-4 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                        <Button
                            variant="outline"
                            onClick={() => syncMutation.reset()}
                            className="rounded-xl px-6 font-bold bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                            Tutup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
